import { useHoverPopover } from '@app/core/hooks';
import { useRouter } from '@mi-front/core';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, List } from '@mui/material';
import { useSidenavContext } from '../SidenavContext';
import { NavItem } from './NavItem';
import { NavItemChild } from './NavItemChild';
import { PopoverTitle } from './PopoverTitle';
import { SidenavPopover } from './SidenavPopover';
import { useNavItemExpanded } from './useNavItemExpanded';

export type NavItemChildConfig = {
  label: string;
  to: string;
  isActive?: (pathname: string) => boolean;
  icon?: React.ReactNode;
};

export type NavItemWithChildrenProps = {
  root: string;
  label: string;
  icon?: React.ReactNode;
  items?: NavItemChildConfig[];
  /** Extra path prefixes that should keep this section expanded and highlighted as active. */
  additionalRoots?: string[];
};

export function NavItemWithChildren({
  label,
  icon,
  root,
  items = [],
  additionalRoots = [],
}: NavItemWithChildrenProps) {
  const { location } = useRouter();
  const { collapsed } = useSidenavContext();
  const {
    anchorEl,
    menuOpen,
    handleMouseEnter,
    handleMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    closePopover,
  } = useHoverPopover({ enabled: collapsed });

  const isActive = [root, ...additionalRoots].some((path) =>
    new RegExp(`^/${path.replace(/^\//, '')}`).test(location.pathname),
  );

  if (!root && items.length === 0) {
    throw new Error('Either to or items must be provided');
  }

  // When there are items, parent link is the first item's target (hub); otherwise use section/station slug
  const to = items.length > 0 ? items[0].to : (root ?? '#');

  const { expanded, toggleExpanded } = useNavItemExpanded(isActive);

  /** When the item has sub-items, clicking the parent must not navigate; toggle items instead. */
  const handleParentClick = (e: React.MouseEvent) => {
    if (items.length > 0) {
      e.preventDefault();
      toggleExpanded();
    }
  };

  const handleChildClick = () => {
    // Close menu when child link is clicked (navigation happens)
    closePopover();
  };

  return (
    <>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: 'block' }}
      >
        <NavItem
          to={to}
          icon={icon}
          isActive={(match, location) => isActive}
          onClick={handleParentClick}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box component="span">{label}</Box>
            {!collapsed && items.length > 0 && (
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ml: 1,
                  transition: 'transform 0.2s',
                }}
              >
                {expanded ? (
                  <ExpandMore sx={{ fontSize: '1.25rem' }} />
                ) : (
                  <ChevronRight sx={{ fontSize: '1.25rem' }} />
                )}
              </Box>
            )}
          </Box>
        </NavItem>
      </Box>
      {items.length > 0 && !collapsed && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {items.map((child, index) => (
              <NavItemChild
                key={index}
                label={child.label}
                to={child.to}
                icon={child.icon}
                isActive={
                  child.isActive
                    ? (match, location) => child.isActive!(location.pathname)
                    : undefined
                }
              />
            ))}
          </List>
        </Collapse>
      )}
      {collapsed && (
        <SidenavPopover
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <PopoverTitle label={label} showBorder={items.length > 0} />
          {items.length > 0 && (
            <List component="div" disablePadding>
              {items.map((child, index) => (
                <Box
                  key={index}
                  onClick={handleChildClick}
                  sx={{
                    '& .MuiListItemButton-root': {
                      pl: 2.5, // Match parent padding instead of 4.25rem
                    },
                  }}
                >
                  <NavItemChild
                    label={child.label}
                    to={child.to}
                    isActive={
                      child.isActive
                        ? (match, location) => child.isActive!(location.pathname)
                        : undefined
                    }
                  />
                </Box>
              ))}
            </List>
          )}
        </SidenavPopover>
      )}
    </>
  );
}
